// Tab-Definition der Kontakt-Detailansicht — aus ContactDetailsRedesign
// herausgelöst, damit die Auflösung von Deep-Links testbar ist.
//
// Der Tab-Name IST gleichzeitig der URL-Parameter (`?tab=…`). Solche Links
// entstehen an Stellen, die wir nicht nachträglich korrigieren können:
// ⌘K-Suche, Lesezeichen der Nutzer und vor allem PERSISTIERTE
// Web-Push-Benachrichtigungen (Backend `contactNote.service.ts` schreibt
// `?tab=Kundentagebuch` in `PendingNotification.link`). Ein Tab darf deshalb nie
// stillschweigend umbenannt werden — alte Namen kommen über TAB_ALIASES weiter
// am richtigen Ziel an.

/** Reihenfolge in der Leiste = Reihenfolge hier. */
export const TABS: readonly string[] = [
  "Allgemein",
  "Kennzahlen",
  "Risiken",
  "Verträge",
  "Schäden",
  // Reine Arbeitsliste: Vorgänge + Todos des Kontakts, ohne Notizen.
  "Vorgänge",
  "Termine",
  "Kommunikation",
  "Zahlungsverkehr",
  // Die Kunden-Chronik: Dauernotiz + Vorgänge + Verlauf/Archiv. Steht bewusst
  // an der Stelle des früheren „Archiv“-Reiters, den sie aufgenommen hat.
  "Kundentagebuch",
  "Dokumente",
];

/**
 * Früherer Tab-Name → aktueller Name.
 *
 * ACHTUNG bei Änderungen: {@link resolveTab} wendet den Alias **vor** der
 * Gültigkeitsprüfung an. Ein Alias-Schlüssel, der auch als echter Tab existiert,
 * macht diesen Tab per URL unerreichbar. Deshalb gibt es hier kein „Vorgänge“
 * mehr — der Name ist seit der Zusammenlegung wieder ein echter Tab.
 */
export const TAB_ALIASES: Record<string, string> = {
  // Der Archiv-Reiter ist im Kundentagebuch als Sektion „Verlauf / Archiv“
  // aufgegangen.
  Archiv: "Kundentagebuch",
  // Der kurzlebige eigene Reiter „Analyse“ ist wieder im Risiken-Reiter
  // aufgegangen (die Analysen-Sektion stand dort durchgehend).
  Analyse: "Risiken",
};

/** Löst einen (ggf. veralteten) Tab-Parameter auf einen gültigen Tab auf. */
export const resolveTab = (raw: string | null): string | null => {
  if (!raw) return null;
  const mapped = TAB_ALIASES[raw] ?? raw;
  return [...TABS, "Produktportfolio", "Protokolle"].includes(mapped) ? mapped : null;
};

/**
 * Reiter, die es nur bei Firmen gibt.
 *
 * „Kennzahlen“ beschreibt Umsatz, Mitarbeiterzahl, Bilanzsummen und Ähnliches —
 * das gibt es bei einer Privatperson nicht. Der Reiter stand dort leer herum
 * (Entscheidung 30.07.2026).
 *
 * ContactDetailsRedesign fängt über visibleTabs/isTabVisible zugleich
 * Deep-Links (`?tab=Kennzahlen` auf einer Person) und Prev/Next-Sprünge von
 * einer Firma auf eine Person ab.
 */
const NUR_FIRMA = new Set<string>(["Kennzahlen"]);

/** Partnerakten: Firmen mit Portfolio, Ansprechpartner ohne Portfolio. */
const PARTNER_TABS: readonly string[] = ["Allgemein", "Vorgänge", "Termine", "Protokolle", "Dokumente"];
const PARTNER_FIRMA_TABS = ["Allgemein", "Produktportfolio", ...PARTNER_TABS.slice(1)];

/** Kontaktstatus, deren Detailseite die reduzierte Partner-Ansicht bekommt. */
const PARTNER_STATUS = new Set<string>([
  "Produktpartner",
  "Ansprechpartner Produktpartner",
  "Kooperationspartner",
  "Ansprechpartner Kooperationspartner",
]);

export const isPartnerStatus = (status?: string | null): boolean =>
  PARTNER_STATUS.has((status ?? "").trim());

/**
 * Die für diesen Kontakt sichtbaren Reiter, in Leisten-Reihenfolge.
 *
 * `status` ist optional, damit bestehende Aufrufer unverändert weiterlaufen —
 * ohne ihn gilt wie bisher allein die Typ-Regel.
 */
export const visibleTabs = (
  isCompany: boolean,
  status?: string | null,
): readonly string[] => {
  if (isPartnerStatus(status)) return isCompany ? PARTNER_FIRMA_TABS : PARTNER_TABS;
  return isCompany ? TABS : TABS.filter((t) => !NUR_FIRMA.has(t));
};

/**
 * Ob ein Reiter bei diesem Kontakt angezeigt werden darf. Nötig, weil der
 * aktive Reiter aus einem Deep-Link oder aus dem Prev/Next-Sprung auf einen
 * anderen Kontakt stammen kann — beides kann auf einem Reiter landen, den es
 * für diesen Kontakt gar nicht gibt.
 */
export const isTabVisible = (
  tab: string,
  isCompany: boolean,
  status?: string | null,
): boolean => {
  return visibleTabs(isCompany, status).includes(tab);
};
